// ----------------------------------------------------------------------------
// Component: Scrolling Panel
// ----------------------------------------------------------------------------
// A GSAP based scrolling section revealing content based on scroll posistion.
// ----------------------------------------------------------------------------
// Debug Helper
// ---------------------------------------------------------------------------
// gsap.delayedCall(0.01, () =>
//     ScrollTrigger.getAll().forEach((t) =>
//         console.log("Start", t.start, "End", t.end, t.vars.id)
//     )
// );
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import './_scrolling-panel.scss';

import { gsap } from 'gsap';

import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// -------------------------------------
// Variables
// -------------------------------------
const SCROLLING_WRAPPER     = '.page-section--scrolling-panel';
const SCROLLLING_PANEL      = '.scrolling-panel';
const PANELS_SECTION        = '.scrolling-panel__section';
const PANEL_IMAGE           = '.scrolling-panel__image';
const PANEL_TEXT            = '.scrolling-panel__content';
const NAV_MENU              = '.scrolling-panel__nav';
const NAV_LINKS             = '.scrolling-panel__nav a';

// Register gsap Plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Main Animation
const ScrollingWrappers = document.querySelectorAll(SCROLLING_WRAPPER);

ScrollingWrappers.forEach((ScrollingWrapper) => {
    const scrollingPanel    = ScrollingWrapper.querySelector(SCROLLLING_PANEL);
    const sections          = Object.values(scrollingPanel.querySelectorAll(PANELS_SECTION));

    // Adjust z-index based on number of panels sections
    gsap.set(sections, {zIndex: (i, target, targets) => targets.length - i});

    const animatePanel = (section, index) => {
        const timeline   = gsap.timeline();
        const panelText  = section.querySelector(PANEL_TEXT);
        const panelImage = section.querySelector(PANEL_IMAGE);
        const nav        = document.querySelector(NAV_MENU);
        
        let firstTextOpacity = 0;
        let firstTextPosition = 50;
        
        if(window.innerWidth < 680) {
            firstTextOpacity = 1;
            firstTextPosition = 0;
        }

        // First Panel
        if (index == 0) {
            timeline
                .from(nav, { opacity: 0, lazy: false }, 'start')
                .from(panelText, { opacity: firstTextOpacity, y: firstTextPosition, lazy: false }, 'start')
                .to(nav, { opacity: 1, duration: 3 }, 'mid')
                .to(panelText, { opacity: 1, y: 0, duration: 5 }, 'mid')
                .to(panelText, { duration: 10 }, 'static')
                .to(panelImage, { duration: 10 }, 'static')
                .to(panelText, { opacity: 0, y: -50, duration: 5 }, 'end')
                .to(panelImage, { opacity: 0,  duration: 5 }, 'end');

        // Last Panel
        } else if (index == sections.length - 1) {
            timeline
                .from(panelText, { opacity: 0, y: 50, lazy: false }, 'start')
                .from(panelImage, { opacity: 0, lazy: false }, 'start')
                .to(panelText, { opacity: 1, y: 0, duration: 5 }, 'mid')
                .to(panelImage, { opacity: 1, duration: 5 }, 'mid')
                .to(panelText, { duration: 7 }, 'static')
                .to(panelImage, { duration: 7 }, 'static')
                .to(nav, { opacity: 0, duration: 7 }, 'static');

        // Mid Panel
        } else {
            timeline
                .from(panelText, { opacity: 0, y: 50, lazy: false }, 'start')
                .from(panelImage, { opacity: 0, lazy: false }, 'start')
                .to(panelText, { opacity: 1, y: 0, duration: 5 }, 'mid')
                .to(panelImage, { opacity: 1, duration: 5 }, 'mid')
                .to(panelText, { duration: 10 }, 'static')
                .to(panelImage, { duration: 10 }, 'static')
                .to(panelText, { opacity: 0, y: -50, duration: 5 }, 'end')
                .to(panelImage, { opacity: 0,  duration: 5 }, 'end');
        }

        return timeline;
    };

    // Pin the entire div that wraps all panels.
    ScrollTrigger.create({
        trigger: scrollingPanel,
        start: 'top top',
        end: '+=' + sections.length * 1500,
        pin: true,
        id: 'pinning'
    });

    sections.forEach((section, index) => {
        const panelSectionId = section.id;

        const timeline = gsap.timeline({
            scrollTrigger: {
                id: panelSectionId,
                trigger: ScrollingWrapper,
                start: 'top top-=' + (index * 1500), // All sections are set on top of the other, so we add up the heights.
                end: '+=' + 1500,
                scrub: true,
                invalidateOnRefresh: true,
                markers: false // Set to true for debbuging start/end points
            }
        });

        timeline.add(animatePanel(section, index));
        
        // In case content gets lazyloaded, recalculate height
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
    });

    // -----------------------------
    // Navigation
    // -----------------------------
    const scrollNav = ScrollingWrapper.querySelectorAll(NAV_LINKS);

    scrollNav.forEach((navLink, index) => {
        const navItem   = navLink.closest('.scrolling-panel__nav-item');
        const navItemId = navItem.id;

        ScrollTrigger.create({
            id: navItemId,
            trigger: ScrollingWrapper,
            start: 'top top-=' + (index * 1500),
            end: '+=' + 1500,
            onToggle: self => self.isActive && setActive(scrollNav, navLink)
        });

        navLink.addEventListener('click', e => {
            e.preventDefault();

            const PanelTrigger = ScrollTrigger.getById(`panel-${navItemId}`);
            const triggerStart =  PanelTrigger.start + 750;

            // Scrolls to mid part of corresponding panel
            gsap.to(window, {duration: 0, scrollTo: triggerStart, overwrite: 'auto'});
        });
    });


    // Refresh ScrollPanel Height when scroll reaches previous section
    ScrollTrigger.create({
        trigger: ScrollingWrapper,
        onEnter: () => {
            refreshScrollTrigger(); // Call refresh when element enters the viewport
        }
    });
});

// -----------------------------
// Helpers Functions
// -----------------------------

// Set Nav Link Active when entering panel
function setActive(nav, link) {
    nav.forEach(el => el.classList.remove('active'));
    link.classList.add('active');
}

// ScrollTrigger refresh function
function refreshScrollTrigger() {
    ScrollTrigger.refresh();
}